/**
 * Toast helpers for WS event side-effects.
 * Extracted from useDrawing.ts to separate presentation (toasts) from
 * business logic (state updates) — R4-P1-2 / R5-A.
 */
import { useToast } from '../state/toast';
import { useAuth } from '../state/auth';
import { usePresence } from '../state/presence';
import { useBoard } from '../state/board';
import { displayName } from '../utils/name';
import type { Stroke } from '@blackboard/shared';

export function toastPresenceJoin(
  selfUserId: string | null,
  user: { user_id: string; display_name?: string | null; username?: string | null },
) {
  if (selfUserId !== null && user.user_id !== selfUserId) {
    useToast.getState().push('info', `${displayName(user)} 进来了`, 2200);
  }
}

export function toastPresenceLeave(selfUserId: string | null, userId: string) {
  const leaving = usePresence.getState().users.get(userId);
  if (leaving && selfUserId !== null && userId !== selfUserId) {
    useToast.getState().push('info', `${displayName(leaving)} 离开了`, 2200);
  }
}

export function eraseAndNotify(
  selfUserId: string | null,
  strokeIds: number[],
  byUserId: string,
  removeStroke: (id: number) => void,
) {
  const idx = useBoard.getState().index;
  let mine = 0;
  for (const id of strokeIds) {
    const stroke = idx.getById(id);
    if (stroke && selfUserId !== null && stroke.user_id === selfUserId) mine++;
    removeStroke(id);
  }
  if (mine > 0 && byUserId !== selfUserId) {
    useToast
      .getState()
      .push(
        'warn',
        `你的 ${mine} 条涂鸦被管理员删除（如有误删可联系管理员恢复）。`,
        5000,
      );
  }
}

export function toastWsError(code: string, reason?: string) {
  if (code === 'stroke_overflow') {
    useToast.getState().push('warn', '笔画过长已截断，请提交后再继续画。', 4500);
  } else if (code === 'erase_too_large') {
    useToast.getState().push('error', '一次擦除的笔画过多，已被服务端拒绝。', 4500);
  } else if (code === 'banned') {
    const until = reason ? new Date(reason) : null;
    const when = until && !Number.isNaN(until.getTime())
      ? until.toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      : '稍后';
    useToast.getState().push('error', `你已被管理员禁止涂鸦，至 ${when}。如有疑问请联系管理员。`, 8000);
    // 同步进 auth store，让画板顶部横幅立即出现（无需刷新）
    const auth = useAuth.getState();
    if (auth.user) useAuth.setState({ user: { ...auth.user, banned_until: reason ?? new Date(Date.now() + 864e5).toISOString() } });
  }
}

/**
 * Build a Stroke from a committed in-progress stroke + server bbox.
 * Returns null if no captured data is available (optimistic self-stroke already removed).
 */
export function buildCommittedStroke(
  e: { remote_id: string; stroke_id: number; bbox: [number, number, number, number] },
  captured: { user_id: string; tool: string; color: string; sizeIdx: number; points: import('@blackboard/shared').Point[] },
): Stroke {
  return {
    id: e.stroke_id,
    board_id: 0,
    user_id: captured.user_id,
    tool: captured.tool as import('@blackboard/shared').Tool,
    color: captured.color,
    size: captured.sizeIdx,
    points: captured.points,
    bbox: { x1: e.bbox[0], y1: e.bbox[1], x2: e.bbox[2], y2: e.bbox[3] },
    created_at: new Date().toISOString(),
    erased_at: null,
    erased_by: null,
  };
}
